import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, Modal, Spinner } from '@heroui/react';
import { DropZone } from '@heroui-pro/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { ExcelImportStage } from './useExcelImport';

type ExcelImportModalViewProps = {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    stage: ExcelImportStage;
    onDrop: (event: {
        items: Array<{ kind: string; getFile?: () => Promise<File> }>;
    }) => void;
    onFilesPicked: (files: ReadonlyArray<File>) => void;
    onTryAgain: () => void;
};

const XLSX_ACCEPT =
    '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

// Drag payloads sometimes lack a usable MIME type; accept generically and let
// the hook's filename/MIME guard reject non-xlsx picks with proper copy.
const getDropOperation = () => 'copy' as const;

/**
 * Render-only "Excel import" modal: drop / select a filled purchase template,
 * watch the analyzing stage, or recover from an error. All flow logic lives in
 * {@link useExcelImport}; the container (`ExcelImportModal`) wires it in.
 */
export const ExcelImportModalView: React.FC<ExcelImportModalViewProps> = ({
    isOpen,
    onOpenChange,
    stage,
    onDrop,
    onFilesPicked,
    onTryAgain,
}) => {
    const { t } = useTranslation('pages');

    return (
        <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
            <Modal.Backdrop>
                <Modal.Container size="md">
                    <Modal.Dialog {...getNodeTestIdProp('excel-import-modal')}>
                        <Modal.CloseTrigger
                            {...getNodeTestIdProp('excel-import-close')}
                        />
                        <Modal.Header
                            {...getNodeTestIdProp('excel-import-header')}
                        >
                            <Modal.Heading
                                {...getNodeTestIdProp('excel-import-title')}
                            >
                                {t('purchases.excelImport.title')}
                            </Modal.Heading>
                            <p className="text-sm text-muted">
                                {t('purchases.excelImport.description')}
                            </p>
                        </Modal.Header>
                        <Modal.Body {...getNodeTestIdProp('excel-import-body')}>
                            {stage.kind === 'select' ? (
                                <DropZone
                                    className="w-full"
                                    {...getNodeTestIdProp('excel-import-zone')}
                                >
                                    {/* Resting fill makes the drop region read
                                        as the modal's target; the drag-over
                                        accent state (built into the component)
                                        is re-asserted so it wins regardless of
                                        CSS layer order. */}
                                    <DropZone.Area
                                        className="min-h-44 w-full bg-surface-secondary data-[drop-target]:border-accent data-[drop-target]:bg-accent-soft"
                                        getDropOperation={getDropOperation}
                                        onDrop={onDrop}
                                        {...getNodeTestIdProp(
                                            'excel-import-zone-area',
                                        )}
                                    >
                                        <DropZone.Icon
                                            className="text-accent"
                                            {...getNodeTestIdProp(
                                                'excel-import-zone-icon',
                                            )}
                                        />
                                        <DropZone.Label
                                            {...getNodeTestIdProp(
                                                'excel-import-zone-label',
                                            )}
                                        >
                                            {t(
                                                'purchases.excelImport.dropLabel',
                                            )}
                                        </DropZone.Label>
                                        <DropZone.Description
                                            {...getNodeTestIdProp(
                                                'excel-import-zone-hint',
                                            )}
                                        >
                                            {t(
                                                'purchases.excelImport.dropHint',
                                            )}
                                        </DropZone.Description>
                                        <DropZone.Trigger
                                            {...getNodeTestIdProp(
                                                'excel-import-zone-trigger',
                                            )}
                                        >
                                            {t(
                                                'purchases.excelImport.selectFile',
                                            )}
                                        </DropZone.Trigger>
                                    </DropZone.Area>
                                    <DropZone.Input
                                        accept={XLSX_ACCEPT}
                                        onSelect={(files) =>
                                            onFilesPicked(
                                                files ? Array.from(files) : [],
                                            )
                                        }
                                        {...getNodeTestIdProp(
                                            'excel-import-zone-input',
                                        )}
                                    />
                                </DropZone>
                            ) : null}

                            {stage.kind === 'analyzing' ? (
                                <div
                                    className="flex min-h-44 flex-col items-center justify-center gap-3"
                                    {...getNodeTestIdProp(
                                        'excel-import-analyzing',
                                    )}
                                >
                                    <Spinner
                                        {...getNodeTestIdProp(
                                            'excel-import-spinner',
                                        )}
                                    />
                                    <p className="text-sm font-medium text-foreground">
                                        {t('purchases.excelImport.analyzing')}
                                    </p>
                                    <p
                                        className="text-xs text-muted"
                                        {...getNodeTestIdProp(
                                            'excel-import-analyzing-file',
                                        )}
                                    >
                                        {stage.fileName}
                                    </p>
                                </div>
                            ) : null}

                            {stage.kind === 'error' ? (
                                <div
                                    className="flex min-h-44 flex-col items-center justify-center gap-3"
                                    {...getNodeTestIdProp('excel-import-error')}
                                >
                                    <Icon
                                        className="size-7 text-danger"
                                        icon="gravity-ui:circle-exclamation"
                                    />
                                    <p
                                        className="text-center text-sm text-foreground"
                                        {...getNodeTestIdProp(
                                            'excel-import-error-message',
                                        )}
                                    >
                                        {t(
                                            `purchases.excelImport.errors.${stage.errorKey}`,
                                        )}
                                    </p>
                                    <Button
                                        onPress={onTryAgain}
                                        size="sm"
                                        variant="secondary"
                                        {...getNodeTestIdProp(
                                            'excel-import-try-again',
                                        )}
                                    >
                                        {t('purchases.excelImport.tryAgain')}
                                    </Button>
                                </div>
                            ) : null}
                        </Modal.Body>
                    </Modal.Dialog>
                </Modal.Container>
            </Modal.Backdrop>
        </Modal>
    );
};
